import { FilterDropdown as SharedFilterDropdown, type FilterColumn } from "@/components/shared/FilterDropdown";
import type { DailyRanksProduct } from "@/types/rankTracker";

/** Keys into the `Record<string, string[]>` `activeFilters` shape the shared composite already
 * uses -- one entry per filter group, AND-ed with each other and with the search box. */
export const THEME_FILTER_COLUMN_ID = "theme";
export const MOVEMENT_FILTER_COLUMN_ID = "movement";
export const ADVERTISING_FILTER_COLUMN_ID = "advertising";

/** The two `Advertising` option values -- a keyword goes into `advertised` exactly when its
 * `hasAdSpend` is true, `not-advertised` otherwise (`sources.md § 3.5`, `§ 5.7`). */
export const ADVERTISED_FILTER_VALUE = "advertised";
export const NOT_ADVERTISED_FILTER_VALUE = "not-advertised";

/** The `Theme` option for keywords in no theme (`themeId: null`) -- not a real theme id, so it
 * cannot collide with one (`glossary.md § Keyword theme`). */
export const NO_THEME_FILTER_VALUE = "no-theme";

export type RankMovementBucket = "big-gains" | "improved" | "unchanged" | "declined" | "big-drops";

const MOVEMENT_BUCKET_LABELS: Record<RankMovementBucket, string> = {
  "big-gains": "Big gains (5+ positions)",
  improved: "Improved",
  unchanged: "Unchanged",
  declined: "Declined",
  "big-drops": "Big drops (5+ positions)",
};

/** Fixed left-to-right order for the five buckets -- never derived from the data. */
const MOVEMENT_BUCKET_ORDER: RankMovementBucket[] = [
  "big-gains",
  "improved",
  "unchanged",
  "declined",
  "big-drops",
];

/**
 * `rankDelta = firstKnown − lastKnown` over the keyword's own `positions`, leading/trailing
 * `null`s trimmed. The only written definition of this rule is
 * `design-input/rank-tracker-heatmap/sources.md:453-455` and `§ 5.3:374` -- restated here because
 * nothing else in the tree carries it.
 *
 * Returns `null` when fewer than two non-`null` days remain after trimming: the keyword has no
 * delta, and the caller must exclude it from every bucket rather than filing it under `Unchanged`
 * (`sources.md § 5.3:374`). Trimming only removes leading/trailing `null`s, so the non-`null` count
 * of the trimmed array equals the non-`null` count of the original -- counting either is the same
 * "known days in the window" the rule means.
 */
export function computeRankMovementBucket(positions: (number | null)[]): RankMovementBucket | null {
  let start = 0;
  let end = positions.length - 1;
  while (start <= end && positions[start] === null) start++;
  while (end >= start && positions[end] === null) end--;
  if (start > end) return null;

  const trimmed = positions.slice(start, end + 1);
  const knownDayCount = trimmed.filter((p) => p !== null).length;
  if (knownDayCount < 2) return null;

  // Trimming guarantees both ends of `trimmed` are non-null.
  const firstKnown = trimmed[0] as number;
  const lastKnown = trimmed[trimmed.length - 1] as number;
  const rankDelta = firstKnown - lastKnown;

  if (rankDelta >= 5) return "big-gains";
  if (rankDelta > 0) return "improved";
  if (rankDelta === 0) return "unchanged";
  if (rankDelta > -5) return "declined";
  return "big-drops";
}

/**
 * One `Theme` filter option per theme present in the brand's full (unfiltered) data, labelled
 * `${name} (${keywordCount})` and sorted by count descending -- computed from the raw response so
 * the option list does not jitter while the user types a search term or narrows another filter.
 *
 * Themes are per hero product, unique on `(productId, name)` (`glossary.md § Keyword theme`), so
 * two different products can each carry a `themeId` that is a different row even when the names
 * collide -- each is its own option. The `No theme` bucket (`themeId: null`) is not a theme and
 * carries no id; every product's bucket collapses into the one `NO_THEME_FILTER_VALUE` option, its
 * count summed across all of them.
 */
export function buildThemeFilterOptions(products: DailyRanksProduct[]): FilterColumn["options"] {
  const totals = new Map<string, { name: string; count: number }>();
  for (const product of products) {
    for (const theme of product.themes) {
      const key = theme.themeId === null ? NO_THEME_FILTER_VALUE : String(theme.themeId);
      const existing = totals.get(key);
      if (existing) {
        existing.count += theme.keywordCount;
      } else {
        totals.set(key, { name: theme.name, count: theme.keywordCount });
      }
    }
  }
  return Array.from(totals.entries())
    .sort((a, b) => b[1].count - a[1].count)
    .map(([value, { name, count }]) => ({ value, label: `${name} (${count})` }));
}

function buildMovementFilterOptions(): FilterColumn["options"] {
  return MOVEMENT_BUCKET_ORDER.map((bucket) => ({ value: bucket, label: MOVEMENT_BUCKET_LABELS[bucket] }));
}

/**
 * The two `Advertising` filter options, labelled `Advertised keyword (${n})` and
 * `Not advertised (${n})` (`sources.md § 3.5`, verbatim format). Both counts are over **every**
 * keyword across all products and themes in the brand's full (unfiltered) response
 * (`sources.md § 5.7`): a keyword is counted `advertised` exactly when `hasAdSpend` is true,
 * `not-advertised` otherwise. Computing them off the raw product list -- like
 * `buildThemeFilterOptions` above -- is what keeps them stable while the theme, movement or search
 * filters narrow the table; they never shrink as another filter is applied.
 */
export function buildAdvertisingFilterOptions(products: DailyRanksProduct[]): FilterColumn["options"] {
  let advertised = 0;
  let notAdvertised = 0;
  for (const product of products) {
    for (const theme of product.themes) {
      for (const keyword of theme.keywords) {
        if (keyword.hasAdSpend) advertised++;
        else notAdvertised++;
      }
    }
  }
  return [
    { value: ADVERTISED_FILTER_VALUE, label: `Advertised keyword (${advertised})` },
    { value: NOT_ADVERTISED_FILTER_VALUE, label: `Not advertised (${notAdvertised})` },
  ];
}

export interface RankHeatmapFilterDropdownProps {
  /** Precomputed by `buildThemeFilterOptions` off the full (unfiltered) product list. */
  themeOptions: FilterColumn["options"];
  /** Precomputed by `buildAdvertisingFilterOptions` off the full (unfiltered) product list -- its
   * counts are over every keyword and do not shrink as another filter narrows the table. */
  advertisingOptions: FilterColumn["options"];
  activeFilters: Record<string, string[]>;
  onFiltersChange: (filters: Record<string, string[]>) => void;
}

/**
 * The heat table's three filter groups, `Theme`, `Rank movement` and `Advertising`, AND-ed with
 * each other and with WEB-006's search box. Built on the shared `components/shared/FilterDropdown`
 * composite -- its trigger, active-count pill, removable chips and `Clear all` link are exactly what
 * this needs, so this file supplies only the rank-tracker-specific column definitions rather than
 * forking that primitive (`conventions/03-frontend-structure.md`).
 *
 * The first group is labelled `Theme`, never `Category`
 * (`decisions/the-thematic-group-is-a-keyword-theme.md`). The third is `Advertising` (AUT-440,
 * `sources.md § 3.5`).
 */
export function FilterDropdown({ themeOptions, advertisingOptions, activeFilters, onFiltersChange }: RankHeatmapFilterDropdownProps) {
  const columns: FilterColumn[] = [
    { id: THEME_FILTER_COLUMN_ID, label: "Theme", options: themeOptions },
    { id: MOVEMENT_FILTER_COLUMN_ID, label: "Rank movement", options: buildMovementFilterOptions() },
    { id: ADVERTISING_FILTER_COLUMN_ID, label: "Advertising", options: advertisingOptions },
  ];

  // AUT-525: pin below the trigger so an expanded Signal AI Rank Report never flips the panel up
  // and clips its top; the panel scrolls instead when space is short.
  return <SharedFilterDropdown columns={columns} activeFilters={activeFilters} onFiltersChange={onFiltersChange} pinBelow />;
}
